import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./customerLotteryRequestModal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faClock, faExclamationCircle, faGolfFlagHole, faXmark } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";
import { IFacility } from "redux/reducers/models/facility";
import { ILottery } from "redux/reducers/models/lottery";
import Divider from "components/divider";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IUserActive } from "redux/reducers/models/user";
import { GetCustomerFriends, ICustomerFriend } from "api/rpc/2024-04/customer/customer";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import { ITeeTime } from "redux/reducers/models/teetime";
import { IPostLotteryRequestParams } from "api/rpc/2024-04/customer/lottery";
import { useGuestPortalContext } from "pages/guest/Portal/GuestPortalContext";
import { ICustomerLotteryRequest, IPutLotteryRequestParams } from "api/rpc/2024-04/customer/lotteryRequest";

interface IProps {
    open: boolean;
    teetime: ITeeTime;
    facility: Partial<IFacility>;
    lottery: ILottery;
    lotteryTimes: Array<ITeeTime>;
    onOk: (params: IPostLotteryRequestParams | IPutLotteryRequestParams) => Promise<void>;
    onCancel: () => void;
    /**Pre fill inputs for editing a request */
    request?: ICustomerLotteryRequest;
    title?: string;
}

interface IState {
    timePreference1: string;
    timePreference2: string;
    timePreference3: string;
    startTimePreferenceWindow: number;
    quantity: number;
    holes: number;
    customerSelection: Array<ICustomerFriend | IUserActive | {id: number; full_name: string}>;
}

interface IFriendState {
    friendSearch: string;
    friendsList: Array<ICustomerFriend>;
    friendSearchResults: Array<ICustomerFriend>;
}

export default function CustomerLotteryRequestModal(props: IProps) {
    const { open, facility, onOk, onCancel, lottery, lotteryTimes, teetime, request, title } = props;
    const { t } = useTranslation(); 
    const { clientPortalStore } = useAppSelector(store => store);
    const { portalState } = useGuestPortalContext();
    const activeUser = clientPortalStore?.user ?? portalState?.guest;
    const { Option } = Select;
    const dispatch = useAppDispatch();
    const [state, setState] = useState<IState>({
        startTimePreferenceWindow: 1,
        timePreference1: "",
        timePreference2: "",
        timePreference3: "",
        quantity: 1,
        holes: 18,
        customerSelection: [],
    });

    const [friendState, setFriendState] = useState<IFriendState>({
        friendSearch: "",
        friendsList: [],
        friendSearchResults: [],
    });

    const [holeButtons, setHoleButtons] = useState([true, true]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            //Pre fill inputs for editing a request
            if (request) {
                const players = presetPlayers();
                setState({
                    startTimePreferenceWindow: request?.start_time_preference_window,
                    timePreference1: request?.start_time_preference_one,
                    timePreference2: request?.start_time_preference_two,
                    timePreference3: request?.start_time_preference_three,
                    quantity: request?.quantity,
                    holes: request?.holes,
                    customerSelection: players?.length > 0 ? players : [activeUser],
                });
            } else {
                //New request
                setState(prev => ({...prev, timePreference1: teetime?.start_time, customerSelection: [activeUser]}));
            }
            
            void getFriendsList(source.token);
            if (teetime){ 
                void determineHolesAvailable();
            }
        } else {
            ReactDOM.unstable_batchedUpdates(() => {
                setState({
                    startTimePreferenceWindow: 1,
                    timePreference1: "",
                    timePreference2: "",
                    timePreference3: "",
                    quantity: 1,
                    holes: 18,
                    customerSelection: [],
                });
                setFriendState({
                    friendSearch: "",
                    friendSearchResults: [],
                    friendsList: [],
                });
            }); 
        }
        return () => source.cancel();
    }, [open]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        let timeoutId: NodeJS.Timeout = null;
        timeoutId = setTimeout(() => {
            void handleSearchFriend(source.token);
          }, 500);
        return () => {
          source.cancel("Cancelled");
          clearTimeout(timeoutId);
          setFriendState(prev => ({...prev, friendSearchResults: []}));
        };
      }, [friendState?.friendSearch]);

    async function handleSearchFriend(cancelToken: CancelToken) {
        if (!friendState?.friendSearch) {
            setFriendState(prev => ({...prev, friendSearchResults: []}));
        } else {
            if (friendState?.friendSearchResults !== undefined) {
                setFriendState(prev => ({...prev, friendSearchResults: undefined}));
                const friendsResults = await searchFriends(friendState?.friendSearch, cancelToken);
                setFriendState(prev => ({...prev, friendSearchResults: friendsResults}));
            }
        }
    }

function presetPlayers() {
    const players = [];
    if (request) {
        if (request?.customer_one) {
            players.push(request?.customer_one);
        }
        if (request?.customer_two) {
            players?.push({...request?.customer_two, full_name: activeUser?.id === request?.customer_id_two ? "Guest" : request?.customer_two?.full_name});
        }
        if (request?.customer_three) {
            players?.push({...request?.customer_three, full_name: activeUser?.id === request?.customer_id_three ? "Guest" : request?.customer_three?.full_name});
        }
        if (request?.customer_four) {
            players?.push({...request?.customer_four, full_name: activeUser?.id === request?.customer_id_four ? "Guest" : request?.customer_four?.full_name});
        }
    }
    return players;
}

  async function getFriendsList(cancelToken: CancelToken) {
    if (friendState?.friendsList !== undefined) {
        setFriendState(prev => ({...prev, friendsList: undefined}));
    }
    const friendsListRes = await GetCustomerFriends({ facility_id: facility?.id }, false, cancelToken);
    if (friendsListRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
            return;
        }
      dispatch(showError(t("Error getting friends list")));
      setFriendState(prev => ({...prev, friendsList: []}));
      return;
    }
    setFriendState(prev => ({...prev, friendsList: friendsListRes?.data}));
  }

  async function searchFriends(playerSearchQuery: string, cancelToken: CancelToken) {
    const searchFriendsRes = await GetCustomerFriends(
      { search: playerSearchQuery, facility_id: facility?.id },
      false,
      cancelToken,
    );
    if (searchFriendsRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
       return [];
      }
      dispatch(showError(t("Error getting friends results")));
      return [];
    }
    return searchFriendsRes.data;
  }

  function renderFriendsList() {
    //Display the search results if the array contains values
    if (friendState?.friendSearchResults?.length > 0) {
      const guestsList: Array<ICustomerFriend> = friendState?.friendSearchResults;
      return guestsList?.filter(guest => !state.customerSelection?.some(participant => participant?.id === guest?.id));
    } else {
      //Else return the entire friends list
      return friendState?.friendsList?.filter(guest => !state.customerSelection?.some(participant => participant?.id === guest?.id));
    }
  }

  function handleAddPlayer(player: ICustomerFriend) {
    const guestsCount = [...state.customerSelection]?.splice(1)?.filter(customer => customer?.id === activeUser?.id)?.length;
    if (guestsCount > 0) {
        let guestUpdated = false;
        const updatedCustomerSelection = [...state.customerSelection]?.reduce((updatedList, currCustomer, index) => {
            if (index > 0 && !guestUpdated) {
                if (currCustomer?.id === activeUser?.id) {
                    //Replace guest with the selected customer
                    guestUpdated = true;
                    return [...updatedList, player];
                }
            }
            return [...updatedList, currCustomer];
        }, [] as Array<ICustomerFriend | IUserActive>);
        ReactDOM.unstable_batchedUpdates(() => {
            setState(prev => ({...prev, customerSelection: [...updatedCustomerSelection]}));
            setFriendState(prev => ({...prev, friendSearch: ""}));
        });
        
    }
  }

  function handleRemovePlayer(index: number) {
    //Replace removed customer with guest
    const updatedCustomerSelection = [...state.customerSelection]?.map((customer, customerIndex) => customerIndex === index ? {...activeUser, full_name: "Guest"} : customer);
    //Sort to put guests at the end of the array
    updatedCustomerSelection.sort((a, b) => {
        if (a.full_name === "Guest" && b.full_name !== "Guest") {
            return 1;
        } 
        if (b.full_name === "Guest" && a.full_name !== "Guest"){
             return -1;
        }
        return a.id - b.id;
      });

    setState(prev => ({...prev, customerSelection: updatedCustomerSelection}));
  }

  function onQuantityChange(newQuantity: number) {
    let updatedCustomerSelection = [...state.customerSelection];
    if (updatedCustomerSelection?.length > newQuantity) {
        updatedCustomerSelection = updatedCustomerSelection?.filter((_, index) => index + 1 <= newQuantity);
        setState(prev => ({...prev, quantity: newQuantity, customerSelection: updatedCustomerSelection}));
    } else {
        const diff = newQuantity - updatedCustomerSelection?.length;
        //Set guests
        for (let i = 0; i < diff; i++) {
            updatedCustomerSelection?.push({...activeUser, full_name: "Guest"});
        }
        setState(prev => ({...prev, quantity: newQuantity, customerSelection: updatedCustomerSelection}));
    }
  }

  function handleOnClose() {
    void onCancel();
  }

  function determineHolesAvailable() {
    const updateHolesButtons = [true, true];
    teetime?.divisions_available?.forEach(division => {
        // 9 holes available
        if (division === 1) {
            updateHolesButtons[1] = false;
        } else if (division === 2) {
          if (
            (teetime?.divisions_available?.length > 1 &&
              teetime?.turn_tee_time === null) ||
            teetime?.turn_tee_time?.blocked_type === "blocked" ||
            ((teetime?.turn_tee_time?.league_id ||
              teetime?.turn_tee_time?.tournament_id) &&
              (teetime?.league_id !==
                teetime?.turn_tee_time?.league_id ||
                teetime?.tournament_id !==
                  teetime?.turn_tee_time?.tournament_id))
          ) {
            // Only 9 holes available
            updateHolesButtons[1] = false;
          } else if (teetime?.turn_tee_time != null) {
            // 18 holes available
            if (
              teetime?.quantity_remaining >= state?.quantity &&
              teetime?.turn_tee_time?.quantity_remaining >= state?.quantity
            ) {
                updateHolesButtons[0] = false;
            } else {
              // Only 9 holes available
              updateHolesButtons[1] = false;
            }
          }
        }
      });

      ReactDOM.unstable_batchedUpdates(() => {
        if (updateHolesButtons[0] && updateHolesButtons[1]) {
            setState(prev => ({...prev, holes: null}));
          } else if (updateHolesButtons[0]) {
            setState(prev => ({...prev, holes: 9}));
          } else if (updateHolesButtons[1]) {
            setState(prev => ({...prev, holes: 18}));
          }
        setHoleButtons(updateHolesButtons);
      });
  }

  function handleOk() {
    const customerIds = state?.customerSelection?.map(customer => customer?.id);
    const guestsCount = [...state.customerSelection]?.splice(1)?.filter(customer => customer?.id === activeUser?.id)?.length;
    if (guestsCount > 0) {
        if (!lottery?.allow_guests) {
            dispatch(showError(t("Guests are not allowed for this lottery. Please add playing partners")));
            return;
        }
    }

    const params = request ? 
    //Edit request
    {
        id: request?.id,
        customer_id: customerIds[0],
        customer_id_one: customerIds[0],
        customer_id_two: customerIds[1] ? customerIds[1] : null,
        customer_id_three: customerIds[2] ? customerIds[2] : null,
        customer_id_four: customerIds[3] ? customerIds[3] : null,
        start_time_preference_window: state?.startTimePreferenceWindow,
        holes: state?.holes,
        quantity: state?.quantity,
        start_time_preference_one: state?.timePreference1,
        start_time_preference_two: state?.timePreference2 ? state?.timePreference2 : null,
        start_time_preference_three: state?.timePreference3 ? state?.timePreference3 : null,
    } : {
        //New request
        lottery_id: lottery?.id,
        facility_id: facility?.id,
        customer_id: customerIds[0],
        customer_id_one: customerIds[0],
        customer_id_two: customerIds[1] ? customerIds[1] : null,
        customer_id_three: customerIds[2] ? customerIds[2] : null,
        customer_id_four: customerIds[3] ? customerIds[3] : null,
        start_time_preference_window: state?.startTimePreferenceWindow,
        holes: state?.holes,
        quantity: state?.quantity,
        start_time_preference_one: state?.timePreference1,
        start_time_preference_two: state?.timePreference2 ? state?.timePreference2 : null,
        start_time_preference_three: state?.timePreference3 ? state?.timePreference3 : null,
    };

    void onOk(params);
  }

    return (
        <Portal
        isMounted={open}
        >
            <Sheet
            title={title ? title : t("Submit Lottery Request")}
            supportText={t("Choose your preferred tee time and playing partners")}
            open={open}
            size="small"
            onCancel={handleOnClose}
            onOk={handleOk}
            okText={request ? t("Save Changes") : t("Submit Request")}
            height="flexible"
            closable
            okDisabled={state?.customerSelection?.length < 1}
            >
                <div className="customer-lottery-request-modal-flex-column">
                    <p className="customer-lottery-request-modal-primary">{t("Request Details")}</p>
                    <div className="customer-lottery-request-modal-flex-row">
                        <div className="customer-lottery-request-modal-secondary"><FontAwesomeIcon icon={faCalendar} fixedWidth /> {moment(teetime?.date, "YYYY-MM-DD")?.format("LL")}</div>
                        <div className="customer-lottery-request-modal-divider"></div>
                        <div className="customer-lottery-request-modal-secondary"><FontAwesomeIcon icon={faClock} fixedWidth /> {moment(teetime?.start_time, "HH:mm")?.format("LT")}</div>
                        <div className="customer-lottery-request-modal-divider"></div>
                        <div className="customer-lottery-request-modal-secondary"><FontAwesomeIcon icon={faGolfFlagHole} fixedWidth /> {facility?.long_name}</div>
                    </div>
                    <p className="customer-lottery-request-modal-secondary"><b>{t("Note: ")}</b> {t("Submissions close")} {moment(lottery?.close_date, "YYYY-MM-DD")?.format("LL")}. 
                    {!lottery?.allow_guests ? t(" Guests are not permitted for this lottery.") : null}</p>
                </div> 
                {lottery?.allow_time_preference_window || lottery?.number_of_time_preferences > 1 ? (
                   <>
                    <Divider />
                    <p className="customer-lottery-request-modal-primary">{t("Tee Time Preferences")}</p>
                   </> 
                ) : null}

                {/* Start time preference window */}
                {lottery?.allow_time_preference_window ? (
                    <FormLayout>
                    <FormLayout.Group>
                    <Select
                        label={t("Start Time Preference Window")}
                        defaultValue={state?.startTimePreferenceWindow}
                        onChange={id => setState(prev => ({...prev, startTimePreferenceWindow: id}))}
                        usePortal
                        >
                            {[
                                {id: 1, label: t("1 Hour")}, 
                                {id: 2, label: t("2 Hours")}, 
                                {id: 3, label: t("3 Hours")}, 
                                {id: 4, label: t("4 Hours")}, 
                                {id: 5, label: t("5 Hours")},
                                {id: 6, label: t("6 Hours")},
                                {id: 7, label: t("7 Hours")},
                                {id: 8, label: t("8 Hours")},
                                ]?.map((window, index) => {
                                return (
                                    <Option key={index} value={window.id}>
                                        {window.label}
                                    </Option>
                                );
                                })}
                    </Select>
                    </FormLayout.Group>
                </FormLayout>
                ) : null}

                {lottery?.number_of_time_preferences > 1 && !lottery?.allow_time_preference_window ? (
                    <FormLayout>
                        <FormLayout.Group>
                            <Select
                                label={t("Second Preferred Time")}
                                defaultValue={state?.timePreference2}
                                usePortal
                                onChange={id => setState(prev => ({...prev, timePreference2: id}))}>
                                    {lotteryTimes?.filter(time => time?.start_time !== state?.timePreference1 && time?.start_time !== state?.timePreference3)?.map((time, index) => {
                                        return (
                                            <Option key={index} value={time?.start_time}>
                                                {moment(time?.start_time, "HH:mm")?.format("LT")}
                                            </Option>
                                        );
                                    })}
                            </Select>

                            {lottery?.number_of_time_preferences > 2 && !lottery?.allow_time_preference_window ? (
                            <Select
                                label={t("Third Preferred Time")}
                                defaultValue={state?.timePreference3}
                                usePortal
                                onChange={id => setState(prev => ({...prev, timePreference3: id}))}>
                                    {lotteryTimes?.filter(time => time?.start_time !== state?.timePreference1 && time?.start_time !== state?.timePreference2)?.map((time, index) => {
                                        return (
                                            <Option key={index} value={time?.start_time}>
                                                {moment(time?.start_time, "HH:mm")?.format("LT")}
                                            </Option>
                                        );
                                    })}
                            </Select>
                        ) : null}
                        </FormLayout.Group>
                    </FormLayout>
                ) : null}

                <Divider />
                <p className="customer-lottery-request-modal-primary">{t("Booking Details")}</p>
                <FormLayout>
                    <FormLayout.Group>
                    <Select
                        label={t("Number of Players")}
                        defaultValue={state?.quantity}
                        usePortal
                        onChange={id => onQuantityChange(id as number)}
                        >
                            {[
                                {id: 1, label: t("1 player")}, 
                                {id: 2, label: t("2 players")}, 
                                {id: 3, label: t("3 players")}, 
                                {id: 4, label: t("4 players")},
                                ]?.map((window, index) => {
                                return (
                                    <Option key={index} value={window.id}>
                                        {window.label}
                                    </Option>
                                );
                            })}
                    </Select>
                    <Select
                        label={t("Holes")}
                        defaultValue={state?.holes}
                        usePortal
                        onChange={id => setState(prev => ({...prev, holes: id}))}
                        >
                            {[
                                {id: 18, label: t("18 holes")}, 
                                {id: 9, label: t("9 holes")}, 
                                ]?.map((window, index) => {
                                return (
                                    <Option key={index} value={window.id} disabled={holeButtons[index] ?? false}>
                                        {window.label}
                                    </Option>
                                );
                            })}
                    </Select>
                    </FormLayout.Group>
                </FormLayout>

                <FormLayout>
                    <FormLayout.Group>
                    <Select
                        showSearch
                        onSearch={(query: string) => setFriendState(prev => ({...prev, friendSearch: query}))}
                        onChange={(_, extraValue: ICustomerFriend) => handleAddPlayer(extraValue)}
                        allowClear
                        searchValue={friendState?.friendSearch}
                        searching={!friendState?.friendSearchResults}
                        showDropDownOnFocus
                        usePortal
                        disabled={[...state.customerSelection]?.splice(1)?.filter(customer => customer?.id === activeUser?.id)?.length === 0}
                        placeholder={t("Search playing partners...")}
                        label={t("Playing Partners")}
                        noData={
                            <div className="flex items-center p-2 text-black">
                            <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                            <span className="ml-3 italic">{t("No friend found...")}</span>
                            </div>
                        }
                        >
                            {renderFriendsList()?.map((friend, index) => {
                                return (
                                    <Option key={index} value={friend?.id} extraValues={friend}>
                                        {friend?.full_name}
                                    </Option>
                                );
                            })}
                        </Select>
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <div>
                            <p className="customer-lottery-request-modal-label">{t("Playing Partners")}</p>
                            <div className="customer-lottery-request-modal-tags-container">
                                {state?.customerSelection?.map((customer, customerIndex) => {
                                    return (
                                        <div key={customerIndex} className="customer-lottery-request-modal-tag">
                                            <p className="customer-tag-text">{customer?.full_name}</p>
                                            {customer?.id !== activeUser?.id ? (
                                                <FontAwesomeIcon icon={faXmark} className="customer-tag-x" onClick={() => handleRemovePlayer(customerIndex)} />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        </FormLayout.Group>
                </FormLayout>
                
            </Sheet>
        </Portal>
    );
}